import { c as _c } from "react/compiler-runtime";
import { postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/request';
import type { DocumentResponse } from '@onefootprint/request-types';
import type { IdDocImageTypes } from '@onefootprint/types';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLogger, trackAction } from '@/idv/utils';
import IdDocAnimation from '../../../components/id-doc-animation';
import Loading from '../../../components/loading';
import RetryLimitExceeded from '../../../components/retry-limit-exceeded';
import Success from '../../../components/success';
import { SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import { IdDocImageUploadError } from '../../../types';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import getRejectionCodes from '../../../utils/get-rejection-codes';
import NextSide from '../../components/next-side';
import useIdDocMachine from '../../hooks/use-id-doc-machine';
const {
  logWarn
} = getLogger({
  location: 'processing'
});
const Processing = () => {
  const $ = _c(69);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "document-flow.id-doc.pages.processing"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t0);
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, send] = useIdDocMachine();
  const submitDocMutation = useSubmitDoc();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = postHostedUserDocumentsByIdProcessMutation();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const processDocMutation = useMutation(t1);
  const [mode, setMode] = useState("loading");
  const [nextSide, setNextSide] = useState();
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState("upload");
  const slowConnectionTimer = useRef(null);
  const {
    idDoc: t2,
    image,
    authToken,
    currSide,
    id
  } = state.context;
  const {
    type,
    country
  } = t2;
  let t3;
  if ($[2] !== country || $[3] !== currSide || $[4] !== id || $[5] !== send || $[6] !== state.context.currSide || $[7] !== type) {
    t3 = data => {
      const {
        errorDetails,
        nextSideToCollect,
        isRetryLimitExceeded
      } = data;
      const rejectionCodes = getRejectionCodes(data);
      const rejectionContext = {
        rejectionCodes,
        docType: type,
        country,
        side: currSide
      };
      if (isRetryLimitExceeded) {
        logWarn(`Image upload retry limit exceeded. Side: ${currSide}, doc id: ${id}`, undefined, rejectionContext);
        trackAction(`document-processing-${currSide}:retry-limit-exceeded`, rejectionContext);
        setRetryLimitExceeded(true);
        return;
      }
      if (nextSideToCollect === state.context.currSide) {
        logWarn(`Document rejected. Side: ${currSide}, codes: ${rejectionCodes}`, undefined, rejectionContext);
        trackAction(`document-processing-${currSide}:rejected`, rejectionContext);
        send({
          type: "processingErrored",
          payload: {
            errors: errorDetails.map(_temp)
          }
        });
        return;
      }
      setMode("success");
      if (nextSideToCollect) {
        setNextSide(nextSideToCollect as IdDocImageTypes);
      }
      trackAction(`document-processing-${currSide}:completed`, rejectionContext);
    };
    $[2] = country;
    $[3] = currSide;
    $[4] = id;
    $[5] = send;
    $[6] = state.context.currSide;
    $[7] = type;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleProcessDocSuccess = t3;
  let t4;
  if ($[9] !== currSide) {
    t4 = () => {
      trackAction(`document-processing-${currSide}:started`);
    };
    $[9] = currSide;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== getErrorMessage || $[13] !== send) {
    t6 = err_0 => {
      const backendMessage = getBackendErrorMessage(err_0);
      send({
        type: "processingErrored",
        payload: {
          errors: [backendMessage ? {
            errorType: "custom_error",
            errorInfo: backendMessage
          } : {
            errorType: IdDocImageUploadError.networkError,
            errorInfo: getErrorMessage(err_0)
          }]
        }
      });
    };
    $[12] = getErrorMessage;
    $[13] = send;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleError = t6;
  let t7;
  if ($[15] !== getErrorMessage || $[16] !== handleError || $[17] !== id) {
    t7 = err_1 => {
      handleError(err_1);
      logWarn(`Error while processing id-doc image ${id}: ${getErrorMessage(err_1)}`, err_1);
    };
    $[15] = getErrorMessage;
    $[16] = handleError;
    $[17] = id;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleProcessDocError = t7;
  let t8;
  if ($[19] !== currSide || $[20] !== getErrorCode || $[21] !== getErrorMessage || $[22] !== handleError || $[23] !== id) {
    t8 = err_2 => {
      const errorMessage = getErrorMessage(err_2);
      const errorCode = getErrorCode(err_2);
      if (errorCode === "E109") {
        setNextSide(undefined);
        setMode("success");
        return;
      }
      handleError(err_2);
      logWarn(`Id-doc image submit failed on phone flow. Side: ${currSide}, upload session id: ${id}. Error: ${errorMessage}`, err_2);
    };
    $[19] = currSide;
    $[20] = getErrorCode;
    $[21] = getErrorMessage;
    $[22] = handleError;
    $[23] = id;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const handleSubmitDocError = t8;
  let t9;
  if ($[25] !== authToken || $[26] !== handleProcessDocError || $[27] !== handleProcessDocSuccess || $[28] !== id || $[29] !== processDocMutation) {
    t9 = async () => {
      if (!id) {
        return;
      }
      setStep("analyze");
      ;
      try {
        const response = await processDocMutation.mutateAsync({
          path: {
            id
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        handleProcessDocSuccess(response);
      } catch (t10) {
        const error = t10;
        handleProcessDocError(error);
      }
    };
    $[25] = authToken;
    $[26] = handleProcessDocError;
    $[27] = handleProcessDocSuccess;
    $[28] = id;
    $[29] = processDocMutation;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const handleSubmitDocSuccess = t9;
  let t10;
  if ($[31] !== authToken || $[32] !== country || $[33] !== currSide || $[34] !== handleSubmitDocError || $[35] !== handleSubmitDocSuccess || $[36] !== id || $[37] !== image || $[38] !== submitDocMutation || $[39] !== type) {
    t10 = () => {
      if (!image || !authToken || !type || !country || !currSide || !id) {
        setIsMissingRequirements(true);
        const error_0 = `Mobile web flow - id-doc image could not be processed due to missing requirements. Requirements - image: ${image ? "OK" : "undefined"}, auth token: ${authToken ? "OK" : "undefined"}, doc type: ${type ? "OK" : "undefined"}, country: ${country ? "OK" : "undefined"}, current side: ${currSide ? "OK" : "undefined"}, id: ${id || "undefined"}`;
        logWarn(error_0);
        return;
      }
      if (!slowConnectionTimer.current) {
        slowConnectionTimer.current = setTimeout(() => {
          if (submitDocMutation.isSuccess) {
            return;
          }
          setShowSlowConnectionMessage(true);
        }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
      }
      const {
        imageFile,
        extraCompressed,
        captureKind,
        forcedUpload
      } = image;
      submitDocMutation.mutate({
        authToken,
        image: imageFile,
        extraCompress: extraCompressed,
        side: currSide,
        forceUpload: forcedUpload,
        id,
        meta: {
          manual: captureKind === "manual",
          isUpload: captureKind === "upload"
        }
      }, {
        onSuccess: handleSubmitDocSuccess,
        onError: handleSubmitDocError,
        onSettled: () => {
          if (slowConnectionTimer.current) {
            clearTimeout(slowConnectionTimer.current);
            setShowSlowConnectionMessage(false);
          }
        }
      });
    };
    $[31] = authToken;
    $[32] = country;
    $[33] = currSide;
    $[34] = handleSubmitDocError;
    $[35] = handleSubmitDocSuccess;
    $[36] = id;
    $[37] = image;
    $[38] = submitDocMutation;
    $[39] = type;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  useEffect(t10, t11);
  let t12;
  if ($[42] !== send) {
    t12 = () => {
      send({
        type: "retryLimitExceeded"
      });
    };
    $[42] = send;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const handleRetryLimitExceeded = t12;
  let t13;
  if ($[44] !== send) {
    t13 = () => {
      send({
        type: "processingSucceeded",
        payload: {
          nextSideToCollect: undefined
        }
      });
    };
    $[44] = send;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const onSuccessComplete = t13;
  let t14;
  if ($[46] !== nextSide || $[47] !== send) {
    t14 = () => {
      send({
        type: "processingSucceeded",
        payload: {
          nextSideToCollect: nextSide
        }
      });
    };
    $[46] = nextSide;
    $[47] = send;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  const onNextSideComplete = t14;
  if (isMissingRequirements) {
    let t15;
    if ($[49] !== t) {
      t15 = t(_temp2);
      $[49] = t;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    let t16;
    if ($[51] !== t15) {
      t16 = <div className="text-label-1 text-error text-center">{t15}</div>;
      $[51] = t15;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    return t16;
  }
  if (retryLimitExceeded) {
    let t15;
    if ($[53] !== handleRetryLimitExceeded) {
      t15 = <RetryLimitExceeded onRetryLimitExceeded={handleRetryLimitExceeded} />;
      $[53] = handleRetryLimitExceeded;
      $[54] = t15;
    } else {
      t15 = $[54];
    }
    return t15;
  }
  if (!type || !currSide) {
    return null;
  }
  let t15;
  if ($[55] !== showSlowConnectionMessage || $[56] !== step) {
    t15 = <Loading step={step} showSlowConnectionMessage={showSlowConnectionMessage} />;
    $[55] = showSlowConnectionMessage;
    $[56] = step;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  const t16 = mode === "success" && !nextSide ? onSuccessComplete : undefined;
  let t17;
  if ($[58] !== t16) {
    t17 = <Success onComplete={t16} />;
    $[58] = t16;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== nextSide || $[61] !== onNextSideComplete) {
    t18 = nextSide && <NextSide nextSideImageType={nextSide} onComplete={onNextSideComplete} />;
    $[60] = nextSide;
    $[61] = onNextSideComplete;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  const t19 = !!nextSide;
  let t20;
  if ($[63] !== mode || $[64] !== t15 || $[65] !== t17 || $[66] !== t18 || $[67] !== t19) {
    t20 = <div className="flex flex-col items-center justify-center h-full min-h-[280px]"><IdDocAnimation loadingComponent={t15} successComponent={t17} nextSideComponent={t18} mode={mode} hasNextSide={t19} /></div>;
    $[63] = mode;
    $[64] = t15;
    $[65] = t17;
    $[66] = t18;
    $[67] = t19;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  return t20;
};
export default Processing;
function _temp(err) {
  return {
    errorType: err.code ?? "custom_error",
    errorInfo: err.message
  };
}
function _temp2($) {
  return $["missing-requirement-error"];
}